/**
 * How a Automations record table lays itself out - pure, so it is tested
 * without a browser.
 *
 * A laptop gets narrower column widths (compactWidth) and may drop columns it
 * can live without (compactHidden). When even those do not fit the width the
 * table actually has - the users panel open beside the workspace, a nested
 * table inside an expanded row - the rows become cards, as on a phone. Sideways
 * scrolling a table is not a layout.
 */

export type BotsLayout = "table" | "cards";

/** A table that just turned into cards turns back only with this much room to
 * spare, so a width on the edge does not flip the layout back and forth. */
export const LAYOUT_HYSTERESIS_PX = 24;

/** Width the one flexible column is assumed to need. */
export const FLEX_COLUMN_MIN_WIDTH = 160;

export interface LayoutColumn {
  width?: number;
  compactWidth?: number;
  compactHidden?: boolean;
}

/** The columns as a laptop shows them: compact widths, compact-hidden dropped. */
export function resolveColumns<C extends LayoutColumn>(
  columns: C[],
  { compact }: { compact: boolean },
): C[] {
  if (!compact) return columns;
  return columns
    .filter((column) => !column.compactHidden)
    .map((column) =>
      column.compactWidth != null ? { ...column, width: column.compactWidth } : column,
    );
}

export function columnsMinWidth(columns: LayoutColumn[]): number {
  return columns.reduce(
    (total, column) => total + (column.width ?? FLEX_COLUMN_MIN_WIDTH),
    0,
  );
}

export function chooseBotsLayout({
  cardMode,
  containerWidth,
  minWidth,
  previous,
}: {
  cardMode: boolean;
  containerWidth: number | null;
  minWidth: number;
  previous: BotsLayout | null;
}): BotsLayout {
  if (cardMode) return "cards";
  if (containerWidth == null || containerWidth <= 0) return previous ?? "table";
  if (previous === "cards") {
    return containerWidth >= minWidth + LAYOUT_HYSTERESIS_PX ? "table" : "cards";
  }
  return containerWidth < minWidth ? "cards" : "table";
}
